import { useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { Button, TextField, FormControl, FormHelperText } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import axios from 'axios';
import ActionConfirmation from './ActionConfirmation';
import { useCart } from '../../contexts/CartContext';
import type { ActionConfirmationVariant } from '../../types';
import ParentsRequest from '../../utils/parentsRequest';
import { MESSAGE_LOGIN_PAYER_ID_UNDEFINED, SESSION_KEY_JWT, SESSION_KEY_USER_DATA } from '../../constants';
import { PaymentMethodParams } from '../../types';
import getPaymentMethods from '../../hooks/user/getPaymentMethods';
import getUser from '../../hooks/user/getUser';
import { useSignModal } from '../../contexts/SignModalContext';
import * as Sentry from '@sentry/react';
import { useScrollToConfirmation } from '../../hooks/useScrollToConfirmation';

const SignModal = () => {
    const { isSignModalOpen, closeSignModal } = useSignModal();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [confirmation, setConfirmation] = useState<{ visible: boolean; variant: ActionConfirmationVariant; message: string }>({ visible: false, variant: 'error', message: '' });
    const [autoHideDuration, setAutoHideDuration] = useState(6000);
    const { updateUserAndSession } = useUser();
    const navigate = useNavigate();
    const { fetchCart, applyPaymentMethod } = useCart();
    const [loading, setLoading] = useState(false);

    const validateForm = () => {
        const isEmailValid = !!email;
        const isPasswordValid = !!password;
        setEmailError(!isEmailValid);
        setPasswordError(!isPasswordValid);
        return isEmailValid && isPasswordValid;
    };

    const handleLogin = async () => {
        setAutoHideDuration(6000);
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const endpoint = import.meta.env.VITE_LOGIN_ENDPOINT;
            const response = await ParentsRequest.post(endpoint, { 'user': email, 'password': password });

            localStorage.setItem(SESSION_KEY_JWT, response.data.data.token);

            const userResponse = await ParentsRequest.get('/users/meta', response.data.data.token);

            if (!userResponse.data.data.payerId) {
                setConfirmation({ visible: true, variant: 'error', message: MESSAGE_LOGIN_PAYER_ID_UNDEFINED });
                setAutoHideDuration(20000);
                return false;
            }

            if (userResponse.data.data.children.length > 0) {
                userResponse.data.data.children = userResponse.data.data.children.map((child: { dob: string; }) => {
                    if (child.dob) {
                        const dateParts = child.dob.split('-');
                        child.dob = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
                    }
                    return child;
                });
            }

            localStorage.setItem(SESSION_KEY_USER_DATA, JSON.stringify(userResponse.data.data));
            updateUserAndSession(userResponse.data.data);

            await fetchCart();

            const paymentMethods = await getPaymentMethods();
            if (paymentMethods && paymentMethods.data) {
                applyPaymentMethod(paymentMethods.data as PaymentMethodParams);
            }

            await getUser();

            closeSignModal();
            navigate('/');
        } catch (error) {
            Sentry.captureException(error);
            if (axios.isAxiosError(error) && error.response) {
                setConfirmation({ visible: true, variant: 'error', message: error.response.data.message || 'Invalid username or password.' });
            } else {
                setConfirmation({ visible: true, variant: 'error', message: 'An unexpected error occurred.' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDismissConfirmation = () => {
        setConfirmation((c) => ({ ...c, visible: false }));
    };

    useScrollToConfirmation(confirmation.visible, confirmation.message, 'sign-modal-confirmation');

    return (
        <Drawer anchor="right" open={isSignModalOpen} onClose={closeSignModal} className="drawer events-drawer">
            <div className="drawer-content px-5 mt-20 position-relative">
                <h2>Welcome to Kindertales</h2>
                <br />
                <p>Ready to Register for Camp?</p>
                <p><b>Have an existing Kindertales account?</b></p>
                <p>Sign in with the email associated with that account.</p>

                <div className="mt-5">
                    <FormControl fullWidth error={emailError} variant="standard" className="mt-10">
                        <TextField
                            required
                            id="email"
                            label="Email"
                            type="email"
                            value={email}
                            variant="standard"
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {emailError && <FormHelperText>Email is required.</FormHelperText>}
                    </FormControl>
                </div>

                <FormControl fullWidth error={passwordError} variant="standard">
                    <TextField
                        required
                        id="password"
                        label="Password"
                        type="password"
                        value={password}
                        variant="standard"
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    {passwordError && <FormHelperText>Password is required.</FormHelperText>}
                </FormControl>

                <div className="mt-20">
                    <p>Don't have an account?{" "}
                        <Link to="/signup" className="text-underline" onClick={closeSignModal}>Create Parent Profile</Link>
                    </p>
                </div>
                <div className="mt-2">
                    <a style={{color: "red", fontSize: 22, backgroundColor: 'rgb(231 231 239)'}} href={import.meta.env.VITE_KINDERTALES_URL + '/login/index.php?pg=fpwd'}>Forgot password?</a>
                </div>

                <Button
                    className="sign-button login-button"
                    onClick={handleLogin}
                    disabled={loading}
                >
                    {loading ? 'Logging in...' : 'Login'}
                </Button>

                <ActionConfirmation
                    id="sign-modal-confirmation"
                    visible={confirmation.visible}
                    variant={confirmation.variant}
                    message={confirmation.message}
                    onDismiss={handleDismissConfirmation}
                    autoHideDuration={autoHideDuration}
                />
            </div>
        </Drawer>
    );
};

export default SignModal;
